import { type FC } from 'react';
import { useTargetBrandConfig } from 'FrontRoyalAngular';
import { type BrandConfig, Brand } from 'AppBranding';
import { twMerge } from 'tailwind-merge';
import { useAudioVisualizer, useTrackAudioData } from '../utils';
import { type SupportedColors, type AudioVisualizerConfig } from '../TutorBotVoiceAgent.types';

type AudioVisualizerPresentationProps = AudioVisualizerConfig & {
    getUserAudioData: ReturnType<typeof useTrackAudioData>['getUserAudioData'];
    getAgentAudioData: ReturnType<typeof useTrackAudioData>['getAgentAudioData'];
    color: SupportedColors | undefined;
};

const AudioVisualizerTheme = {
    coral: {
        // If you find yourself searching for these colors,
        // we should ask if they should be named and added to our common colors.
        inner: '#FF3850', // Vibrant coral-red
        outer: '#FF7676', // Soft luminous coral-pink
    },
    blue: {
        inner: '#2B44FF', // Rich royal blue
        outer: '#60A5FA', // Light sky blue
    },
    purple: {
        inner: '#8B31FF',
        outer: '#B87FFF',
    },
} as const;

const getAudioVisualizerTheme = ({ brandConfig, color }: { brandConfig: BrandConfig; color: 'purple' | undefined }) => {
    let key: keyof typeof AudioVisualizerTheme;
    if (color === 'purple') {
        key = 'purple';
    } else {
        key = brandConfig.branding === Brand.valar ? 'blue' : 'coral';
    }
    return AudioVisualizerTheme[key];
};

// used in ConnectedLiveKitRoomUi story
export const AudioVisualizerWrapper = ({ children }: { children: React.ReactNode }) => (
    <div className={twMerge('flex h-full flex-col items-center justify-center')}>
        <div className="relative mx-auto flex animate-fade-in flex-col items-center justify-center overflow-visible">
            <div style={{ width: '282px', height: '282px' }}>{children}</div>
        </div>
    </div>
);

export const AudioVisualizerPresentation: FC<AudioVisualizerPresentationProps> = ({
    getUserAudioData,
    getAgentAudioData,
    color,
}) => {
    const maskRadiusSizeFactor = 0.4;
    const hexagonGradientStart = 'rgba(255,255,255,0.8)';
    const hexagonGradientEnd = 'rgba(255,255,255,0.2)';
    const hexagonStrokeColor = 'rgba(255,255,255,0.9)';
    const hexagonSizeFactor = 0.5;

    const brandConfig = useTargetBrandConfig();
    const gradientColors = getAudioVisualizerTheme({ brandConfig, color });

    const canvasRef = useAudioVisualizer({
        maskRadiusSizeFactor,
        gradientColors,
        hexagonGradientStart,
        hexagonGradientEnd,
        hexagonStrokeColor,
        hexagonSizeFactor,
        getUserAudioData,
        getAgentAudioData,
    });

    return (
        <AudioVisualizerWrapper>
            <canvas ref={canvasRef} />
        </AudioVisualizerWrapper>
    );
};

export const AudioVisualizer: FC<{ color: SupportedColors | undefined }> = props => {
    const { getUserAudioData, getAgentAudioData } = useTrackAudioData();

    return (
        <AudioVisualizerPresentation
            {...props}
            getUserAudioData={getUserAudioData}
            getAgentAudioData={getAgentAudioData}
        />
    );
};
